import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { History, GitCompare, X, Loader2, Plus, Minus, Repeat, Check } from "lucide-react";

export function formatPeriodLabel(period) {
  if (!period) return "";
  const [y, m] = period.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString(undefined, { month: "short", year: "numeric", timeZone: "UTC" });
}

export function MonthPicker({ periods, value, latestPeriod, onChange, onCompare }) {
  if (!periods?.length) return null;
  return (
    <div className="flex items-center gap-2" data-testid="ai-insights-month-picker">
      <div className="relative">
        <History className="h-3.5 w-3.5 text-[#a0aec0] absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
        <select
          value={value || latestPeriod || ""}
          onChange={(e) => onChange(e.target.value === latestPeriod ? null : e.target.value)}
          data-testid="ai-insights-month-select"
          className="bg-white border border-[#e2e8f0] rounded-xl pl-8 pr-3 py-2 text-xs font-semibold text-[#4a5568] focus:outline-none focus:border-[#4fd1c5]"
        >
          {periods.map((p) => (
            <option key={p.period} value={p.period}>
              {formatPeriodLabel(p.period)}
              {p.period === latestPeriod ? " (latest)" : ""}
              {p.versions > 1 ? ` · ${p.versions} versions` : ""}
            </option>
          ))}
        </select>
      </div>
      {periods.length > 1 && (
        <button type="button" onClick={onCompare} className="btn-ghost inline-flex items-center gap-2" data-testid="ai-insights-compare-open">
          <GitCompare className="h-3.5 w-3.5" /> Compare months
        </button>
      )}
    </div>
  );
}

const norm = (t) => (t || "").toLowerCase().replace(/[^a-z0-9 ]+/g, " ").replace(/\s+/g, " ").trim();

// Items in `newer` tagged new / carried over; items only in `older` returned as dropped.
function diffItems(older = [], newer = []) {
  const oldKeys = new Set(older.map((i) => norm(i.title)));
  const newKeys = new Set(newer.map((i) => norm(i.title)));
  return {
    current: newer.map((i) => ({ ...i, tag: oldKeys.has(norm(i.title)) ? "carried" : "new" })),
    dropped: older.filter((i) => !newKeys.has(norm(i.title))),
  };
}

const TAG = {
  new: { label: "New", icon: Plus, cls: "bg-[#e6fffa] text-[#234e52] border-[#81e6d9]" },
  carried: { label: "Carried over", icon: Repeat, cls: "bg-[#f7fafc] text-[#4a5568] border-[#e2e8f0]" },
};

const DROPPED_LABEL = {
  wins: "No longer listed",
  concerns: "Resolved / dropped",
  opportunities: "No longer listed",
  recommended_actions: "Dropped from list",
};

function DiffSection({ kind, label, older, newer, olderLabel }) {
  const { current, dropped } = diffItems(older, newer);
  return (
    <div className="card-soft p-4" data-testid={`ai-compare-section-${kind}`}>
      <div className="text-[11px] uppercase tracking-widest text-[#4a5568] font-bold mb-3">
        {label} <span className="font-mono-tight text-[#a0aec0] normal-case tracking-normal">· {newer.length} vs {older.length}</span>
      </div>
      <ul className="space-y-2">
        {current.map((i, idx) => {
          const t = TAG[i.tag];
          const Icon = t.icon;
          return (
            <li key={`c-${idx}`} className="text-[13px] text-[#1a202c] flex items-start gap-2">
              <span className={`shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[9px] font-bold uppercase tracking-wider ${t.cls}`}>
                <Icon className="h-2.5 w-2.5" /> {t.label}
              </span>
              <span className="flex-1">
                {i.title}
                {i.state === "done" && <Check className="inline h-3 w-3 ml-1 text-[#166534]" strokeWidth={3} />}
              </span>
            </li>
          );
        })}
        {dropped.map((i, idx) => (
          <li key={`d-${idx}`} className="text-[13px] text-[#718096] flex items-start gap-2">
            <span className="shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[9px] font-bold uppercase tracking-wider bg-[#fff5f5] text-[#9b2c2c] border-[#feb2b2]">
              <Minus className="h-2.5 w-2.5" /> {DROPPED_LABEL[kind]}
            </span>
            <span className="flex-1 line-through decoration-[#cbd5e0]">{i.title}</span>
          </li>
        ))}
        {!current.length && !dropped.length && <li className="text-[12px] italic text-[#a0aec0]">Nothing in either month.</li>}
      </ul>
      <div className="text-[10px] text-[#a0aec0] mt-3">Compared with {olderLabel}</div>
    </div>
  );
}

function ValueDelta({ a, b }) {
  const va = a?.traffic_value?.gbp_per_month || 0;
  const vb = b?.traffic_value?.gbp_per_month || 0;
  if (!va && !vb) return null;
  const d = vb - va;
  return (
    <div className="text-xs text-[#4a5568]" data-testid="ai-compare-traffic-value">
      Est. PPC value: <span className="font-semibold">£{Math.round(va).toLocaleString()}</span> →{" "}
      <span className="font-semibold">£{Math.round(vb).toLocaleString()}</span>{" "}
      <span className={d >= 0 ? "text-[#166534]" : "text-[#c53030]"}>({d >= 0 ? "+" : "−"}£{Math.abs(Math.round(d)).toLocaleString()})</span>
    </div>
  );
}

export function CompareMonthsPanel({ clientId, periods, onClose }) {
  const [older, setOlder] = useState(periods[1]?.period);
  const [newer, setNewer] = useState(periods[0]?.period);
  const [docs, setDocs] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const need = [older, newer].filter((p) => p && !docs[p]);
        const res = await Promise.all(need.map((p) => api.get(`/clients/${clientId}/ai-insights?period=${p}`)));
        if (alive) setDocs((d) => ({ ...d, ...Object.fromEntries(need.map((p, i) => [p, res[i].data.insight])) }));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, older, newer]);

  const a = docs[older];
  const b = docs[newer];
  const sel = (val, set, testid) => (
    <select value={val} onChange={(e) => set(e.target.value)} data-testid={testid}
      className="bg-white border border-[#e2e8f0] rounded-xl px-3 py-1.5 text-xs font-semibold text-[#4a5568] focus:outline-none focus:border-[#4fd1c5]">
      {periods.map((p) => <option key={p.period} value={p.period}>{formatPeriodLabel(p.period)}</option>)}
    </select>
  );

  return (
    <div className="card-soft p-5 space-y-4 border border-[#4fd1c5]/30" data-testid="ai-compare-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 text-sm font-semibold text-[#1a202c]">
          <GitCompare className="h-4 w-4 text-[#319795]" /> Compare
          {sel(older, setOlder, "ai-compare-older")} <span className="text-[#a0aec0]">→</span> {sel(newer, setNewer, "ai-compare-newer")}
        </div>
        <button type="button" onClick={onClose} className="text-[#a0aec0] hover:text-[#1a202c]" data-testid="ai-compare-close"><X className="h-4 w-4" /></button>
      </div>
      {loading || !a || !b ? (
        <div className="py-6 text-center"><Loader2 className="h-4 w-4 animate-spin mx-auto text-[#4fd1c5]" /></div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {[[older, a], [newer, b]].map(([p, d]) => (
              <div key={p} className="rounded-xl border border-[#edf2f7] bg-[#f7fafc] p-4" data-testid={`ai-compare-headline-${p}`}>
                <div className="text-[10px] uppercase tracking-widest text-[#a0aec0] font-bold">{formatPeriodLabel(p)}</div>
                <div className="font-display text-base text-[#1a202c] mt-1 leading-snug">{d.insight?.headline}</div>
              </div>
            ))}
          </div>
          <ValueDelta a={a} b={b} />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {[["wins", "Wins"], ["concerns", "Concerns"], ["opportunities", "Opportunities"], ["recommended_actions", "Recommended actions"]].map(([k, l]) => (
              <DiffSection key={k} kind={k} label={l} older={a.insight?.[k] || []} newer={b.insight?.[k] || []} olderLabel={formatPeriodLabel(older)} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
